import React from "react";
import { Link } from "react-router-dom";
import { HeartHandshake, Sparkles, Compass, Target, ArrowRight } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_site-builder-3580/artifacts/8f1fzcms_image.png";

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#1a0a2e] via-[#240a3d] to-[#2e0e54]" />
        <div className="max-w-5xl mx-auto px-5 lg:px-10 py-24 lg:py-32 text-center">
          <div className="mx-auto h-32 w-32 rounded-full bg-[#f7f2e7] flex items-center justify-center ring-1 ring-[rgba(212,175,55,0.45)] overflow-hidden">
            <img src={LOGO_URL} alt="" className="h-[88%] w-[88%] object-contain" />
          </div>
          <div className="text-[#f0d479] text-xs tracking-[0.32em] uppercase mt-6">About Beds of Haven</div>
          <h1 className="font-serif text-5xl md:text-6xl mt-3 text-[#f7f2e7] leading-tight">
            Healing Lives. <span className="gold-text">Building Futures.</span>
            <br />Creating Haven.
          </h1>
          <p className="text-[#dcd0b3] mt-7 max-w-2xl mx-auto text-lg leading-relaxed">
            Beds of Haven is an Independent Shared Housing Program built on a
            simple belief: every adult deserves a safe, dignified place to call
            home — and the steady ground to grow from.
          </p>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-6xl mx-auto px-5 lg:px-10 grid md:grid-cols-2 gap-10">
          <div className="card-purple p-10">
            <Compass className="text-[#d4af37]" size={30} />
            <h2 className="font-serif text-3xl mt-4 text-[#f7f2e7]">Our Mission</h2>
            <p className="text-[#dcd0b3] mt-4 leading-relaxed">
              To provide comfortable, supportive, and safe shared homes for
              adults 30 and older — empowering them to live independently,
              regain stability, and pursue meaningful futures.
            </p>
          </div>
          <div className="card-purple p-10">
            <Target className="text-[#d4af37]" size={30} />
            <h2 className="font-serif text-3xl mt-4 text-[#f7f2e7]">Our Vision</h2>
            <p className="text-[#dcd0b3] mt-4 leading-relaxed">
              A community where transitions are met with dignity, where every
              resident feels respected, and where independent living becomes
              the launching pad for a brighter, stronger tomorrow.
            </p>
          </div>
        </div>
      </section>

      <section className="section-pad bg-[#15082a]">
        <div className="max-w-6xl mx-auto px-5 lg:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[#f0d479] text-xs tracking-[0.32em] uppercase">Our Values</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 text-[#f7f2e7]">
              The pillars of <span className="gold-text">a Haven</span>.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {values.map((v) => (
              <div key={v.title} className="card-purple p-7">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#d4af37] to-[#b8902c] flex items-center justify-center text-[#1a0a2e]">
                  <v.Icon size={20} />
                </div>
                <div className="font-serif text-2xl text-[#f7f2e7] mt-4">{v.title}</div>
                <div className="text-[#cdbfa1] mt-2 text-sm leading-relaxed">{v.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-5xl mx-auto px-5 lg:px-10">
          <div className="text-center">
            <div className="text-[#f0d479] text-xs tracking-[0.32em] uppercase">FAQ</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 text-[#f7f2e7]">
              Common <span className="gold-text">questions</span>
            </h2>
          </div>

          <div className="mt-12 space-y-4">
            {faqs.map((f, i) => (
              <FaqItem key={f.q} {...f} idx={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-gradient-to-br from-[#240a3d] to-[#3d1465]">
        <div className="max-w-4xl mx-auto px-5 lg:px-10 text-center">
          <HeartHandshake className="mx-auto text-[#d4af37]" size={42} />
          <h2 className="font-serif text-4xl md:text-5xl mt-5 text-[#f7f2e7]">
            Step into your <span className="gold-text">Haven</span>.
          </h2>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <Link to="/apply" data-testid="about-cta-apply" className="btn-gold">
              Begin Application <ArrowRight size={18} />
            </Link>
            <Link to="/contact" data-testid="about-cta-contact" className="btn-outline-gold">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

const values = [
  { title: "Dignity", desc: "Every resident is welcomed with respect and treated as the capable adult they are.", Icon: HeartHandshake },
  { title: "Stability", desc: "Predictable, well-maintained homes create the foundation real progress requires.", Icon: Compass },
  { title: "Growth", desc: "We celebrate small wins and big breakthroughs on the road to independence.", Icon: Sparkles },
];

const faqs = [
  { q: "Who is eligible to apply?", a: "Adults age 30 and older with verified guaranteed income who are committed to independent living, personal growth, and respecting our shared-home community." },
  { q: "What's included in the home?", a: "Fully furnished private or shared rooms, utilities, Wi-Fi, on-site laundry, and access to comfortable common areas — all in safe, convenient neighborhoods." },
  { q: "Is this transitional housing?", a: "Beds of Haven is independent shared housing. Residents live with autonomy in a structured, supportive environment that encourages stability and accountability." },
  { q: "How do I apply?", a: "Submit our online application or call us directly at 706-431-8996. Each application is reviewed personally and we follow up with next steps." },
  { q: "How long can I stay?", a: "Length of stay is discussed individually. Our goal is to support you for as long as you need to build the next phase of your independent life." },
];

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div
      data-testid={`faq-item-${idx}`}
      className="card-purple overflow-hidden"
    >
      <button
        onClick={() => setOpen(!open)}
        data-testid={`faq-toggle-${idx}`}
        className="w-full flex items-center justify-between gap-6 p-6 text-left"
      >
        <span className="font-serif text-xl text-[#f7f2e7]">{q}</span>
        <span className={`text-[#d4af37] text-2xl transition-transform ${open ? "rotate-45" : ""}`}>+</span>
      </button>
      {open && (
        <div className="px-6 pb-6 text-[#dcd0b3] leading-relaxed border-t border-[rgba(212,175,55,0.18)] pt-4">
          {a}
        </div>
      )}
    </div>
  );
}
